/**
 * Which links the outcome hero shows beside "Close this cycle".
 *
 * The hero has one primary action — closing — and room for inline links under it.
 * Two were rendered unconditionally, and on most surfaces they went to the same
 * screen: the outcome action's `onPress` is `reviewRecordedCycleAction` for every
 * unresolved result and for any result already acknowledged, which is exactly
 * where the neighbouring "Review this cycle" link goes. On an acknowledged result
 * they even carried the same words — `stage_action_review_records` and
 * `cycle_closing.ui.review_cycle` are both "Revoir ce cycle" — so the hero told
 * her the same thing twice.
 *
 * One link, then. Which one depends on whether the outcome action is still saying
 * something of its own:
 *
 * - **The acknowledge step.** On a recorded positive or negative result the action
 *   acknowledges the result, which is what opens pregnancy follow-up. It is a real
 *   second thing to do, so both links stay.
 * - **A result that is missing or unsettled.** "Add or review the result",
 *   "Review the result details" — the only place Home asks for a result it does
 *   not have. Same destination as the generic link, but not the same sentence, so
 *   this one is kept and the generic one steps aside.
 * - **Everything else**, a recorded loss above all: "Revoir ce qui a été
 *   enregistré" says nothing "Revoir ce cycle" does not. The generic link stays.
 *
 * Pure and separate from the component because the rule is four states wide and
 * decides whether a patient can reach her own result; the component only renders
 * what it returns.
 */

import type { PostProcedureOutcomeStage } from "../../cycles/presenters/postProcedureOutcomeState";

export type OutcomeHeroLinks = {
  /** Render the hero's own outcome action as an inline link. */
  showsOutcomeAction: boolean;
  /** Render the generic "Review this cycle" link. */
  showsReviewCycleLink: boolean;
};

export function resolveOutcomeHeroLinks(input: {
  /** The Home variant, e.g. `ivf-outcome-positive`. */
  variant: string;
  /** `recorded` until the patient has acknowledged the result. */
  outcomeStage: PostProcedureOutcomeStage;
  /**
   * Whether the hero resolved an outcome action at all. False leaves only the
   * generic link, which is what kept a closed or negative ending — and a partner
   * on any of them — with a route into the cycle.
   */
  hasOutcomeAction: boolean;
}): OutcomeHeroLinks {
  const isUnresolved =
    input.variant.endsWith("-unavailable") ||
    input.variant.endsWith("-uncertain") ||
    input.variant.endsWith("-adverse");

  /** The result itself is what is missing; the action asks for it. */
  const asksForTheResult =
    input.variant.endsWith("-unavailable") ||
    input.variant.endsWith("-uncertain");

  const onlyOpensCycle = isUnresolved || input.outcomeStage !== "recorded";

  const showsOutcomeAction =
    input.hasOutcomeAction && (!onlyOpensCycle || asksForTheResult);

  return {
    showsOutcomeAction,
    // Only ever dropped in favour of the link above, never on its own: a hero
    // with no way into the cycle is the failure this rule exists to avoid.
    showsReviewCycleLink: !(asksForTheResult && input.hasOutcomeAction),
  };
}
